import React from "react";
import Card from "components/card";
import Button from "components/button/Button";
import DropzoneFile from "components/dropzone";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import SwitchField from "components/fields/SwitchField";
import NumFormatField from "components/fields/NumFormatField";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import { ICourierCreate } from "./types/courier.interface";
import { addCourier, getCourier, updateCourier } from "api/CourierAPI";

const CourierForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [files, setFiles] = React.useState<File[]>([]);
  const [defaultImg, setDefaultImg] = React.useState<string>("");
  const [convertedWeight, setConvertedWeight] = React.useState<string>("0");

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const watchIsThirdParty = useWatch({
    control,
    name: "is_third_party",
  });

  const watchWeightChange = useWatch({
    control,
    name: "max_weight",
  });

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    setIsLoading(true);

    let maxDistance = null;
    let maxWeight = null;

    if (!watchIsThirdParty) {
      maxDistance = !isNaN(parseFloat(data.max_distance))
        ? parseFloat(data.max_distance)
        : null;
      maxWeight = !isNaN(parseFloat(data.max_weight))
        ? parseInt(data?.max_weight.split(" ")[0].replace(".", ""))
        : null;
    }

    const payload: ICourierCreate = {
      name: data.name,
      description: data.description,
      image: files[0] ?? null,
      is_active: data.is_active,
      max_distance: maxDistance,
      max_weight: maxWeight,
      code: data.code,
      is_third_party: data.is_third_party,
    };

    let promise = null;

    if (!id) promise = addCourier(payload);
    else promise = updateCourier(id, payload);

    toast.promise(promise, {
      loading: "Menyimpan data kurir...",
      success: () => {
        navigate("/admin/couriers");
        return "Berhasil menyimpan data kurir!";
      },
      error: (err: any) =>
        err?.response?.data?.message || "Gagal menyimpan data kurir!",
    });

    setIsLoading(false);
  };

  const getDetailData = (slug: string): void => {
    const promise = getCourier(slug);

    toast.promise(promise, {
      loading: "Mengambil data kurir...",
      success: (res) => {
        if (res.status === 200) {
          if (res.data.data.image) setDefaultImg(res.data.data.image);
          reset(res.data.data);

          return res.data.message || "Berhasil mengambil data kurir!";
        }
      },
      error: (err) => {
        console.error(err);
        return err?.response?.data?.message || "Gagal mengambil data kurir!";
      },
    });
  };

  React.useEffect(() => {
    if (id) getDetailData(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (watchWeightChange) {
      const trimWeight = watchWeightChange
        .toString()
        .replace(/ gram/g, "")
        .replace(".", "");
      const convertWeight = parseFloat(trimWeight) / 1000;
      setConvertedWeight(convertWeight.toString());
    } else setConvertedWeight("0");
  }, [watchWeightChange]);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Kurir
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
        <div className="mt-8">
          <div className="flex flex-col">
            <label htmlFor="images" className="label">
              Foto Kurir
            </label>
            <DropzoneFile
              text="Foto Kurir"
              accept={{
                "image/*": [".jpg", ".png", ".jpeg", ".webp"],
              }}
              maxSize={2048000}
              maxFiles={1}
              files={files}
              dropFile={(files: File[]) => dropFile(files)}
              removeFile={(files: File[]) => removeFile(files)}
              arrangeFiles={(files: File[]) => arrangeFiles(files)}
              changeFile={(files: File[]) => changeFile(files)}
            />
          </div>
          {defaultImg && (
            <div className="my-3 flex flex-col">
              <label htmlFor="images" className="label">
                Foto Lama Kurir
              </label>
              <div className="mx-auto flex h-48 w-auto flex-col items-center justify-center rounded-md border border-gray-300">
                <img
                  src={`${process.env.REACT_APP_PUBLIC_URL}/` + defaultImg}
                  alt="coupon"
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            </div>
          )}
          <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
            Detail Kurir
          </p>
          <div className="flex w-fit gap-7 px-3">
            <SwitchField
              label="Status Kurir Aktif"
              desc=""
              id="is_active"
              mb="mb-3"
              mt=""
              placeholder="Status Kurir Aktif"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
            <SwitchField
              label="Pihak Ketiga"
              desc=""
              id="is_third_party"
              mb="mb-3"
              mt=""
              placeholder="Pihak Ketiga"
              register={register}
              errors={errors}
              validationSchema={{}}
            />
          </div>
          <InputField
            extra="mb-3"
            label="Nama Kurir"
            placeholder="Masukkan nama kurir"
            id="name"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Nama Kurir harus diisi!",
            }}
          />
          <InputField
            extra="mb-3"
            label="Kode Kurir"
            placeholder="Masukkan kode kurir"
            id="code"
            type="text"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{
              required: "Kode Kurir harus diisi!",
            }}
          />
          {!watchIsThirdParty && (
            <div className="mb-3 grid grid-cols-12 gap-4">
              <Controller
                name="max_distance"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <NumFormatField
                    field={field}
                    extra="col-span-12 lg:col-span-6"
                    decimalSeparator=","
                    thousandSeparator="."
                    errors={errors}
                    rules={{}}
                    label="Jarak Maksimal (km)"
                    id="max_distance"
                    placeholder="Masukkan jarak maksimal kurir"
                    variant="outline"
                    suffix=" km"
                  />
                )}
              />
              <div className="relative col-span-12 lg:col-span-6">
                <Controller
                  name="max_weight"
                  control={control}
                  rules={{}}
                  render={({ field }) => (
                    <NumFormatField
                      field={field}
                      extra=""
                      decimalSeparator=","
                      thousandSeparator="."
                      errors={errors}
                      rules={{}}
                      label="Berat Maksimal (gram)"
                      id="max_weight"
                      placeholder="Masukkan berat maksimal barang"
                      variant="outline"
                      suffix=" gram"
                    />
                  )}
                />
                <small className="absolute left-3 top-full mt-1 font-semibold text-gray-600">
                  Dalam kg ={" "}
                  <span className="text-brand-500">{convertedWeight} kg</span>
                </small>
              </div>
            </div>
          )}
          <TextField
            extra="mb-3 flex-1 mt-10 lg:mt-0"
            label="Deskripsi"
            placeholder="Deskripsi kurir"
            id="description"
            variant="outline"
            register={register}
            errors={errors}
            validationSchema={{}}
          />
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default CourierForm;
